import { ArrowLeft, FileText } from 'lucide-react';

export const processOptions = ['Microwaved', 'Oven', 'Frozen', 'Refrigerated'];
export const substrateOptions = ['Polypropylene', 'PET', 'CPET', "I don't know"];

const FilmWizard = ({
  step,
  onStepChange,
  selectedProcesses,
  onToggleProcess,
  selectedSubstrate,
  onSubstrateChange,
  onShowResults,
  filteredFilms,
  selectedFilmItems,
  onToggleFilmSelection,
  filmProducts,
  filmNotes,
  onFilmNotesChange,
  filmContact,
  onFilmContactChange,
  onSubmit,
  openPdf
}) => {
  if (step === 'questions') {
    return (
      <div className="max-w-4xl mx-auto">
        <h2 className="text-5xl font-bold uppercase mb-4 text-brandOrange text-center">
          Find Your Perfect Sealing Film
        </h2>
        <p className="text-xl text-gray-700 mb-12 text-center">
          Answer a few quick questions to find the ideal film for your application.
        </p>

        <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-gray-100">
          <div className="mb-8">
            <h3 className="text-2xl font-semibold uppercase mb-6 text-gray-800">
              Select all of the processes your product uses:
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {processOptions.map((process) => {
                const isSelected = selectedProcesses.includes(process);
                return (
                  <button
                    key={process}
                    onClick={() => onToggleProcess(process)}
                    className={`p-6 rounded-md text-xl font-semibold transition-colors border-2 ${
                      isSelected
                        ? 'text-white border-transparent'
                        : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-orange-300'
                    }`}
                    style={
                      isSelected
                        ? {
                            background:
                              'linear-gradient(270deg,#ffa400,#f78900 15.67%,#f07100 33.69%,#eb6000 52.89%,#e85600 73.87%,#e75300)'
                          }
                        : {}
                    }
                  >
                    {process}
                  </button>
                );
              })}
            </div>
          </div>

          {selectedProcesses.length > 0 && (
            <div className="mb-8">
              <h3 className="text-2xl font-semibold uppercase mb-6 text-gray-800">
                What container or substrate are you using?
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {substrateOptions.map((substrate) => {
                  const isSelected = selectedSubstrate === substrate;
                  return (
                    <button
                      key={substrate}
                      onClick={() => onSubstrateChange(substrate)}
                      className={`p-6 rounded-lg text-lg font-semibold transition-colors border-2 ${
                        isSelected
                          ? 'text-white border-transparent'
                          : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-orange-300'
                      }`}
                      style={
                        isSelected
                          ? {
                              background:
                                'linear-gradient(270deg,#ffa400,#f78900 15.67%,#f07100 33.69%,#eb6000 52.89%,#e85600 73.87%,#e75300)'
                            }
                          : {}
                      }
                    >
                      {substrate}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {selectedProcesses.length > 0 && selectedSubstrate && (
            <div className="text-center">
              <button
                onClick={onShowResults}
                className="text-white px-8 py-4 mt-6 tracking-wide rounded-md text-xl uppercase font-semibold transition-all duration-200 hover:scale-105 shadow-lg"
                style={{
                  background:
                    'linear-gradient(270deg,#ffa400,#f78900 15.67%,#f07100 33.69%,#eb6000 52.89%,#e85600 73.87%,#e75300)'
                }}
              >
                Show Compatible Films
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (step === 'results') {
    const handleFilmCardKeyDown = (event, filmId) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onToggleFilmSelection(filmId);
      }
    };

    const renderFilmCard = (film) => {
      const isSelected = selectedFilmItems.includes(film.id);

      return (
        <div
          key={film.id}
          role="button"
          tabIndex={0}
          onClick={() => onToggleFilmSelection(film.id)}
          onKeyDown={(event) => handleFilmCardKeyDown(event, film.id)}
          aria-pressed={isSelected}
          className={`relative flex h-full flex-col gap-4 rounded-lg border-2 p-6 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
            isSelected
              ? 'border-orange-500 bg-orange-50 shadow-lg'
              : 'border-gray-200 bg-white hover:border-orange-300 hover:shadow-md'
          }`}
          style={isSelected ? { borderColor: '#e75300' } : undefined}
        >
          <h3 className="text-2xl font-semibold text-gray-900 leading-tight">
            <span className="text-orange-600">{film.name}</span>
          </h3>

          <div className="space-y-2 text-sm text-gray-600 flex-grow">
            <p>
              <span className="font-semibold text-gray-800">Thickness:</span> {film.mil} mil ({film.micron} micron)
            </p>
            <p>
              <span className="font-semibold text-gray-800">Substrates:</span>{' '}
              {
                [
                  film.pp && 'PP',
                  film.apetPet && 'APET/PET',
                  film.cpet && 'CPET',
                  film.pe && 'PE',
                  film.paper && 'Paper'
                ]
                  .filter(Boolean)
                  .join(', ') || 'N/A'
              }
            </p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 pt-2 text-sm">
              {film.peelable && <p className="text-green-700">✓ Peelable</p>}
              {film.weld && film.weld !== '' && (
                <p className="text-green-700">
                  <span className="font-semibold">✓ Weld:</span> {film.weld}
                </p>
              )}
              {film.highBarrier && <p className="text-green-700">✓ High Barrier</p>}
              {film.antifogging && <p className="text-green-700">✓ Anti-Fog</p>}
              {film.perforation && <p className="text-green-700">✓ Perforation</p>}
              {film.printable && <p className="text-green-700">✓ Printable</p>}
              {film.microwavable && <p className="text-green-700">✓ Microwavable</p>}
              {film.freezable && <p className="text-green-700">✓ Freezable</p>}
              {film.ovenable && <p className="text-green-700">✓ Ovenable</p>}
            </div>
          </div>

          {film.specSheet && (
            <button
              type="button"
              onClick={() => openPdf(film.specSheet)}
              className="inline-flex items-center gap-2 self-start rounded-full border border-transparent px-4 py-2 text-sm font-semibold text-white shadow-sm transition"
              style={{
                background:
                  'linear-gradient(270deg,#ffa400,#f78900 15.67%,#f07100 33.69%,#eb6000 52.89%,#e85600 73.87%,#e75300)'
              }}
            >
              <FileText size={16} />
              View Spec Sheet
            </button>
          )}

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onToggleFilmSelection(film.id);
            }}
            className={`mt-auto inline-flex items-center justify-center gap-2 w-full rounded-full border px-4 py-2 text-sm font-semibold uppercase transition ${
              isSelected
                ? 'border-transparent text-white shadow-sm'
                : 'border-orange-400 text-orange-600 hover:bg-orange-50'
            }`}
            style={
              isSelected
                ? {
                    background:
                      'linear-gradient(270deg,#ffa400,#f78900 15.67%,#f07100 33.69%,#eb6000 52.89%,#e85600 73.87%,#e75300)'
                  }
                : {}
            }
          >
            {isSelected ? 'In Kit' : 'Add to My Kit'}
          </button>
        </div>
      );
    };

    const hasProcessSelections = selectedProcesses.length > 0;
    const hasSubstrateSelection = Boolean(selectedSubstrate);
    const hasSelections = hasProcessSelections || hasSubstrateSelection;

    return (
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <button
            onClick={() => onStepChange('questions')}
            className="flex items-center gap-2 text-orange-600 hover:text-orange-700 mb-4"
            style={{ color: '#e75300' }}
          >
            <ArrowLeft size={20} />
            Use Guided Quiz
          </button>
          <h2 className="text-5xl font-bold uppercase mb-4 text-gray-800 text-center" style={{ color: '#e75300' }}>
            {hasSelections ? 'Compatible Films' : 'All Films'}
          </h2>
          {hasSelections ? (
            <p className="text-center text-xl text-gray-700">
              Based on your selections
              {hasProcessSelections && (
                <>
                  :{' '}
                  <span className="font-semibold" style={{ color: '#e75300' }}>
                    {selectedProcesses.join(', ')}
                  </span>{' '}
                  {selectedProcesses.length === 1 ? 'process' : 'processes'}
                </>
              )}
              {hasSubstrateSelection && selectedSubstrate !== "I don't know" && (
                <>
                  {hasProcessSelections ? ' with ' : ': '}
                  <span className="font-semibold" style={{ color: '#e75300' }}>
                    {selectedSubstrate}
                  </span>{' '}
                  substrate
                </>
              )}
            </p>
          ) : (
            <p className="text-center text-xl text-gray-700">
              Browse our complete catalog of sealing films. Use the guided quiz if you'd like a tailored list.
            </p>
          )}
        </div>

        {filteredFilms.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100 text-center text-gray-700 mb-8">
            No films match your current selections. Try adjusting the guided quiz for different results.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {filteredFilms.map(renderFilmCard)}
          </div>
        )}

        <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100 mt-8">
          <h3 className="text-2xl font-semibold mb-6 text-gray-800">Request More Information</h3>
          {selectedFilmItems.length > 0 && (
            <>
              <p className="text-gray-600 mb-2">
                You have selected {selectedFilmItems.length} film
                {selectedFilmItems.length > 1 ? 's' : ''}:
              </p>
              <ul className="list-disc list-inside mb-6 text-sm text-gray-700 pl-4">
                {selectedFilmItems.map((itemId) => {
                  const item = filmProducts.find((p) => p.id === itemId);
                  return <li key={itemId}>{item ? item.name : `ID: ${itemId}`}</li>;
                })}
              </ul>
            </>
          )}
          <form onSubmit={onSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">
                What attributes do you need the film to have? (e.g., high barrier, anti-fog, etc.)
              </label>
              <textarea
                value={filmNotes}
                onChange={(e) => onFilmNotesChange(e.target.value)}
                rows={3}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="Please describe any specific requirements..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Name *</label>
                <input
                  type="text"
                  required
                  value={filmContact.name}
                  onChange={(e) => onFilmContactChange({ ...filmContact, name: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="First and last name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Company *</label>
                <input
                  type="text"
                  required
                  value={filmContact.company}
                  onChange={(e) => onFilmContactChange({ ...filmContact, company: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Company name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Email *</label>
                <input
                  type="email"
                  required
                  value={filmContact.email}
                  onChange={(e) => onFilmContactChange({ ...filmContact, email: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Phone *</label>
                <input
                  type="tel"
                  required
                  value={filmContact.phone}
                  onChange={(e) => onFilmContactChange({ ...filmContact, phone: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            <button
              type="submit"
              className="w-full text-white px-6 py-3 rounded-lg font-semibold uppercase tracking-wide transition-all duration-200 hover:scale-[1.02] shadow-lg"
              style={{
                background:
                  'linear-gradient(270deg,#ffa400,#f78900 15.67%,#f07100 33.69%,#eb6000 52.89%,#e85600 73.87%,#e75300)'
              }}
            >
              Submit Inquiry
            </button>
          </form>
        </div>
      </div>
    );
  }

  return null;
};

export default FilmWizard;
